import React, { useState, useMemo, useRef, useCallback, useEffect, memo } from 'react';
import { StyleSheet, View, Pressable, Platform, Dimensions, LayoutChangeEvent, useWindowDimensions, Keyboard } from 'react-native';
import { ScrollView } from 'react-native';
import { XStack, Text } from 'tamagui';
import GenerateArray, { WEB_DENSE_ICON_GAP, WEB_DENSE_ICON_SIZE } from './Components/GenerateArray';
import SearchBarBackButton from './Components/SearchBarBackButton';
import { SearchComponentTypes } from './Types';
import InteractiveSearch from './Components/InteractiveSearch';
import { useNavigation } from '@react-navigation/native';
import { Feather, FontAwesome, MaterialIcons, Ionicons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import LoadingIndicator from '../Spinner';
import { useDispatch } from 'react-redux';
import { useDeviceType } from '@hooks/useDeviceType';
import { AICreditsModal } from '@components/modals/SaaSModals';
import { useBillingInfoWithContext, useCompanyNumber } from '@utils/billingUtils';
import { useQueryClient } from '@tanstack/react-query';
import { getPillButtonStyle, responsivePadding, responsiveHeight } from '@stylesheet';
import { FrostedGlassGlow, getFrostedGlassStyle } from '@components/Global/FrostedCard/FrostedGlass';
import { useTranslation } from 'react-i18next';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { SHOW_AUTH_MODAL, OPEN_MENU } from '@reduxLocal/actions/types';
import { canSafelyGoBack } from '@hooks/backButtonCallback';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSpring,
  interpolate,
  Extrapolation,
  Easing,
  runOnJS,
  cancelAnimation,
} from 'react-native-reanimated';
import SearchBarPaginationInline, {
  SearchBarPaginationModalBlock,
} from './Components/SearchBarPagination';
import Pagination from '@components/Global/Pagination/Pagination';

/** Light secondary chips cannot hold a white glyph. Treat white as unset so the chip tint wins. */
const isNearWhiteIcon = (color: unknown) => {
  if (typeof color !== 'string') return false;
  const value = color.trim().toLowerCase();
  return value === 'white' || value === '#fff' || value === '#ffffff';
};

const iconColorOnLightChip = (explicit: unknown, fallback: string, lightTheme: boolean) => {
  if (lightTheme && Platform.OS === 'web' && (explicit == null || isNearWhiteIcon(explicit))) {
    return '#3b82f6';
  }
  return (typeof explicit === 'string' ? explicit : undefined) ?? fallback;
};

// Enhanced responsive breakpoints
const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;
const AI_CALLBACK_TIMEOUT_MS = 60000;
const AI_PROGRESS_NOTICE_MS = 15000;

/** Material-style dense search row height on web (dp-equivalent via responsiveHeight). */
const WEB_SEARCH_ROW_DENSE_DP = 42;
const WEB_SEARCH_EXPANDED_MAX_DP = 320;

const getWebExpandedHeight = (screenHeight: number, hasCollapsable: boolean) => {
  const cap = responsiveHeight(hasCollapsable ? 400 : WEB_SEARCH_EXPANDED_MAX_DP);
  const viewportFraction = hasCollapsable ? 0.45 : 0.35;
  return Math.min(cap, screenHeight * viewportFraction);
};

/** Shared burger visibility (keeps pagination effect from referencing `showBurgerMenu` before init). */
function computeShowBurgerMenu(p: {
  customIcons: SearchComponentTypes['customIcons'];
  disableBurger: boolean | undefined;
  isTablet: boolean;
  fullWidthOnTablet: boolean;
  baseContainerWidth: number;
  responsiveConfig: {
    padding: { horizontal: number };
    buttonSize: number;
    spacing: { horizontal: number };
    showAllComponents: boolean;
    maxVisibleComponents: number;
    searchMinWidth: number;
  };
  otherComponents: SearchComponentTypes['otherComponents'];
  extraComponents: SearchComponentTypes['extraComponents'];
  aiSearch: boolean;
}): boolean {
  const {
    customIcons,
    disableBurger,
    isTablet,
    fullWidthOnTablet,
    baseContainerWidth,
    responsiveConfig,
    otherComponents,
    extraComponents,
    aiSearch,
  } = p;
  const hasAnyActions =
    (customIcons?.length ?? 0) > 0 || (otherComponents?.length ?? 0) > 0 || !!extraComponents;
  if (!hasAnyActions || disableBurger) return false;
  if (Platform.OS === 'web') return false;
  if (isTablet && !fullWidthOnTablet) return false;
  // Native phones (non-tablet): always burger; web already returned above.
  if (!isTablet) return true;
  if (isTablet && fullWidthOnTablet && (customIcons?.length ?? 0) >= 3) {
    return true;
  }
  const containerPadding = isTablet && fullWidthOnTablet ? 20 : responsiveConfig.padding.horizontal;
  const maxContainerWidth = baseContainerWidth;
  const iconSize = responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal;
  const uncappedIconWidth = (customIcons?.length || 0) * iconSize;
  const iconContainerWidthCap = aiSearch ? 100 : 200;
  const iconContainerWidth = Math.min(uncappedIconWidth, iconContainerWidthCap);
  let totalReservedWidth = containerPadding * 2;
  const hasOtherComponents = otherComponents && otherComponents.length > 0;
  if (hasOtherComponents) {
    const maxOtherComponents = responsiveConfig.showAllComponents
      ? (otherComponents?.length || 0)
      : responsiveConfig.maxVisibleComponents;
    const otherComponentsWidth = Math.min(
      maxOtherComponents * (responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal),
      aiSearch ? 150 : 250,
    );
    totalReservedWidth += otherComponentsWidth;
  }
  totalReservedWidth += iconContainerWidth;
  const availableWidth = maxContainerWidth - totalReservedWidth;
  const iconsPercentage = (iconContainerWidth / maxContainerWidth) * 100;
  const wouldExceedSearchMin = availableWidth < responsiveConfig.searchMinWidth;
  return uncappedIconWidth > iconContainerWidthCap || iconsPercentage > 25 || wouldExceedSearchMin;
}

const SearchComponent = ({
  removeFlex = false,
  aiCallback,
  placeHolder,
  aiSearch = false,
  searchState,
  setSearchState,
  searchOnly = false,
  minWidth,
  backButtonCallback,
  onlyOthers = false,
  searchBarWidth,
  removeBackgroundColor = false,
  customIcons,
  disableFilter,
  otherComponents,
  customIconStyle,
  backButton,
  backButtonStyle,
  showSearch = true,
  actionButton,
  action,
  searchParams,
  customStyle,
  marginBelow,
  callback,
  helpComponent,
  fetchingState,
  extraComponents,
  rightExtraComponents,
  inverse,
  disableBurger,
  fullWidthOnTablet = false,
  compact = false,
  lightTheme,
  pagination,
  collapsableComponent,
  collapsedContent,
  isExpanded: externalIsExpanded,
  onExpandChange,
  hideInput,
  pageTitle,
  searchInputReplacement,
}: SearchComponentTypes) => {
  const [extendedMenu, setExtendedMenu] = useState(false);
  const [showExtendedMenu, setShowExtendedMenu] = useState(extendedMenu);
  const [containerLayout, setContainerLayout] = useState({ width: 0, height: 0 });
  const [searchBarMeasuredWidth, setSearchBarMeasuredWidth] = useState(0);
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded;

  const setIsExpanded = useCallback((expanded: boolean) => {
    if (onExpandChange) {
      onExpandChange(expanded);
    }
    setInternalIsExpanded(expanded);
  }, [onExpandChange]);

  const [isFocused, setIsFocused] = useState(false);

  // Sync expanded height with isExpanded state
  useEffect(() => {
    if (shouldExpand) {
      if (isExpanded) {
        expandedHeight.value = withTiming(expandedHeightValue, ANIMATION_CONFIG);
        backdropOpacity.value = withTiming(1, ANIMATION_CONFIG);
      } else {
        // Only collapse if it was actually expanded (avoids double animation or unwanted cleanup)
        if (expandedHeight.value > 0) {
          expandedHeight.value = withTiming(0, { duration: 250 });
          backdropOpacity.value = withTiming(0, { duration: 250 });
        }
      }
    }
  }, [isExpanded, collapsedContent]);

  const [showAICreditsModal, setShowAICreditsModal] = useState(false);
  const [aiWandLoading, setAiWandLoading] = useState(false);
  const [liveSearchValue, setLiveSearchValue] = useState(searchState || '');
  const [paginationExpanded, setPaginationExpanded] = useState(false);

  // Billing info for AI credits check
  const companyNumber = useCompanyNumber();
  const billingInfo = useBillingInfoWithContext();



  const buttonRef = useRef<any>(null);
  const containerRef = useRef<View>(null);
  const searchContainerRef = useRef<View>(null);
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const reduxLightTheme = themeSelector();
  const theme = lightTheme !== undefined ? lightTheme : reduxLightTheme;
  const { isTablet } = useDeviceType();
  const queryClient = useQueryClient();

  // Use useWindowDimensions hook for better performance
  const screenDimensions = useWindowDimensions();

  // Animation values for expansion (only when aiCallback or collapsableComponent exists)
  const shouldExpand = !!aiCallback || !!collapsableComponent;
  const expandedHeight = useSharedValue(0);
  const backdropOpacity = useSharedValue(0);
  const containerScale = useSharedValue(1);
  const containerTop = useSharedValue(0);
  const baseContainerHeight = useSharedValue(0);

  const collapsableComponentRef = useRef<any>(null);

  // Initialize search state
  useEffect(() => {
    if (setSearchState && !searchState) {
      setSearchState('');
    }
  }, [setSearchState, searchState]);

  // Keep local button-state value aligned with external state updates.
  useEffect(() => {
    setLiveSearchValue(searchState || '');
  }, [searchState]);

  // Initialize containerTop to base position when aiCallback is present
  // Since position is handled at screen level, use the original top position
  useEffect(() => {
    if (shouldExpand) {
      const baseTop = Platform.OS === 'ios' ? 15 : 20;
      containerTop.value = baseTop;
    }
  }, [shouldExpand, containerTop]);

  // Memoized search callback
  const handleSearchCallback = useCallback((text: string) => {
    setLiveSearchValue(text || '');
    if (setSearchState) {
      setSearchState(text || '');
    }
    if (callback) {
      callback(text);
    }
  }, [setSearchState, callback]);

  // Keep parent state in sync immediately for expanded button states.
  const handleInputValueChange = useCallback((text: string) => {
    setLiveSearchValue(text || '');
    if (setSearchState) {
      setSearchState(text || '');
    }
  }, [setSearchState]);

  // Check if user has enough AI credits before allowing generation
  const checkAICredits = useCallback(() => {
    // If billing info is still loading, allow the call (will be checked on backend)
    if (billingInfo?.aiUsage?.isLoading) {
      console.log('🔄 [AI Credits] Billing info loading, allowing call (backend will validate)');
      return true;
    }

    // If user has active subscription, they have unlimited credits
    if (billingInfo?.hasActiveSubscription) {
      console.log('✅ [AI Credits] Active subscription - unlimited credits');
      return true;
    }

    // For free plan users, check usage stats
    const currentUsage = billingInfo?.aiUsage?.current || 0;
    const limit = billingInfo?.aiUsage?.limit || 5;
    const remainingCredits = limit - currentUsage;

    console.log('🔍 [AI Credits] Check:', {
      currentUsage,
      limit,
      remainingCredits,
      hasCredits: remainingCredits > 0,
      billingInfo: billingInfo?.aiUsage
    });

    // Check if user has credits remaining (remainingCredits > 0)
    return remainingCredits > 0;
  }, [billingInfo]);

  // Wrapped AI callback that checks credits before executing
  const wrappedAICallback = useCallback(async (text: string) => {
    // Check if user has enough credits
    if (!checkAICredits()) {
      console.log('❌ [AI Credits] Insufficient credits, showing modal');
      // Show credits modal
      setShowAICreditsModal(true);
      return false;
    }

    // If credits are available, proceed with AI callback
    if (aiCallback) {
      let progressNoticeTimeout: NodeJS.Timeout | null = null;
      try {
        console.log('✅ [AI Credits] Credits available, proceeding with AI generation');
        progressNoticeTimeout = setTimeout(() => {
          dispatch({
            type: SHOW_AUTH_MODAL,
            data: {
              type: 'warning',
              content: 'Your AI request is still running in the background. We will notify you when it completes.',
            },
          });
        }, AI_PROGRESS_NOTICE_MS);
        const aiResult = await Promise.race([
          aiCallback(text),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('AI request timed out. Please try again.')), AI_CALLBACK_TIMEOUT_MS);
          }),
        ]);
        if (progressNoticeTimeout) {
          clearTimeout(progressNoticeTimeout);
          progressNoticeTimeout = null;
        }

        // Generic scheduled-task detection for wand-triggered background jobs.
        const payload = (aiResult as any)?.data ?? aiResult;
        const status = String(payload?.status || '').toLowerCase();
        const hasTaskId = !!(payload?.taskId || payload?.task_id || payload?.requestId || payload?.request_id);
        if (status === 'processing' || status === 'queued' || hasTaskId) {
          dispatch({
            type: SHOW_AUTH_MODAL,
            data: {
              type: 'success',
              content: 'Task scheduled in the background. You will be notified once it completes.',
            },
          });
        }

        // Refetch billing info after successful AI call to update credit count
        // Use a small delay to allow backend to update the count
        setTimeout(() => {
          console.log('🔄 [AI Credits] Refetching billing info after AI call');
          // Invalidate all related queries to ensure fresh data
          queryClient.invalidateQueries({ queryKey: ['aiUsageStats'] });
          queryClient.invalidateQueries({ queryKey: ['subscriptionPlan'] });
          if (companyNumber) {
            queryClient.invalidateQueries({ queryKey: ['subscriptionPlan', companyNumber] });
          }
          // Force refetch immediately
          queryClient.refetchQueries({ queryKey: ['aiUsageStats'] });
        }, 1500);
        return true;
      } catch (error) {
        console.error('❌ [AI Credits] AI callback error:', error);
        if (progressNoticeTimeout) {
          clearTimeout(progressNoticeTimeout);
          progressNoticeTimeout = null;
        }
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            type: 'error',
            content: `AI generation failed. ${(error as any)?.message || 'Please try again.'}`,
            details: (error as any)?.response?.data || (error as any)?.message || error,
          },
        });
        // Even on error, refetch billing info in case it was partially processed
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['aiUsageStats'] });
        }, 1000);
        return false;
      }
    }
    return false;
  }, [aiCallback, checkAICredits, queryClient, dispatch]);

  // Animation configuration
  const ANIMATION_CONFIG = useMemo(() => ({
    duration: 300,
    easing: Easing.out(Easing.cubic),
  }), []);

  const SPRING_CONFIG = useMemo(() => ({
    damping: 15,
    stiffness: 150,
    mass: 1,
    overshootClamping: false,
  }), []);

  // Expanded height calculation
  const expandedHeightValue = useMemo(() => {
    if (!shouldExpand) return 0;
    if (Platform.OS === 'web') {
      return getWebExpandedHeight(screenDimensions.height, !!collapsableComponent);
    }
    if (collapsableComponent) return screenDimensions.height * 0.6;
    return screenDimensions.height * 0.4;
  }, [shouldExpand, screenDimensions.height, collapsableComponent]);

  // Handle input focus - expand when aiCallback exists
  const handleInputFocus = useCallback(() => {
    if (!shouldExpand) return;

    setIsFocused(true);
    setIsExpanded(true);

    // Animate expansion - only expand height, keep top position unchanged
    expandedHeight.value = withTiming(expandedHeightValue, ANIMATION_CONFIG);
    backdropOpacity.value = withTiming(1, ANIMATION_CONFIG);
    // Don't use scale transform - it causes shifting
    // containerScale.value = withSpring(1.02, SPRING_CONFIG);
    // Keep top position at base - don't move down
  }, [shouldExpand, expandedHeightValue, expandedHeight, backdropOpacity, ANIMATION_CONFIG]);

  // Handle input blur
  const handleInputBlur = useCallback(() => {
    if (!shouldExpand) return;
    // Don't blur if expanded (user might be interacting with other elements)
    if (!isExpanded) {
      setIsFocused(false);
    }
  }, [shouldExpand, isExpanded]);

  const finishCollapseUi = useCallback(() => {
    setIsFocused(false);
    setIsExpanded(false);
  }, []);

  // Handle collapse — keep isExpanded true until height animation finishes so layout and
  // expandedHeight stay in sync (avoids a tall empty shell or stuck height after AI submit).
  const handleCollapse = useCallback(() => {
    if (!shouldExpand) return;

    cancelAnimation(expandedHeight);
    cancelAnimation(backdropOpacity);

    backdropOpacity.value = withTiming(0, { duration: 250 });
    expandedHeight.value = withTiming(0, {
      duration: 250,
    }, (finished) => {
      if (finished) {
        expandedHeight.value = 0;
        runOnJS(finishCollapseUi)();
      }
    });

    Keyboard.dismiss();
  }, [shouldExpand, expandedHeight, backdropOpacity, finishCollapseUi]);

  // Handle backdrop press
  const handleBackdropPress = useCallback(() => {
    handleCollapse();
  }, [handleCollapse]);

  const handleClearSearch = useCallback(() => {
    setLiveSearchValue('');
    if (setSearchState) setSearchState('');
    if (callback) callback('');
  }, [setSearchState, callback]);

  const handleWandPress = useCallback(async () => {
    const currentQuery = (liveSearchValue || '').trim();
    if (!currentQuery || !wrappedAICallback) return;
    try {
      setAiWandLoading(true);
      const success = await wrappedAICallback(currentQuery);
      if (success) {
        handleClearSearch();
        handleCollapse();
      }
    } catch (err) {
      console.error('AI search error:', err);
    } finally {
      setAiWandLoading(false);
    }
  }, [liveSearchValue, wrappedAICallback, handleClearSearch, handleCollapse]);

  // Animated styles
  const containerAnimatedStyle = useAnimatedStyle(() => {
    // When collapsed (height is 0 or very small), use base container height
    // This ensures consistent height during collapse animation and prevents position shifts
    if (expandedHeight.value === 0 || expandedHeight.value < 10) {
      return {
        minHeight: baseContainerHeight.value,
        height: undefined, // Allow to grow if wrapped
      } as any;
    }

    // When expanded, set the height to the expanded value
    // Since container is absolutely positioned, this expansion overlays content
    // and doesn't push it down in the layout flow
    return {
      height: expandedHeight.value,
      // Ensure overflow is visible so expanded content can show
      overflow: 'visible' as const,
    } as any;
  }, []);

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  // Enhanced responsive utilities
  const currentBreakpoint = useMemo(() => {
    const width = screenDimensions.width;
    if (width >= BREAKPOINTS.xxl) return 'xxl';
    if (width >= BREAKPOINTS.xl) return 'xl';
    if (width >= BREAKPOINTS.lg) return 'lg';
    if (width >= BREAKPOINTS.md) return 'md';
    if (width >= BREAKPOINTS.sm) return 'sm';
    return 'xs';
  }, [screenDimensions.width]);

  // Responsive configuration based on breakpoint
  const responsiveConfig = useMemo(() => {
    const isNativeMobile = Platform.OS !== 'web' && !isTablet;
    const configs = {
      xs: {
        containerHeight: Platform.OS === 'android' ? 48 : 44,
        searchBorderRadius: 8,
        iconSize: isNativeMobile ? 16 : 18,
        buttonSize: isNativeMobile ? 36 : 32,
        minWidth: !hideInput && 500,
        spacing: { horizontal: 6, vertical: 4 },
        padding: { horizontal: 12, vertical: 4 },
        maxContainerWidth: screenDimensions.width - 24,
        searchMinWidth: 240,
        showAllComponents: false,
        maxVisibleComponents: 2,
      },
      sm: {
        containerHeight: Platform.OS === 'android' ? 52 : 48,
        searchBorderRadius: 8,
        iconSize: isNativeMobile ? 18 : 20,
        buttonSize: isNativeMobile ? 38 : 36,
        minWidth: !hideInput && 500,
        spacing: { horizontal: 8, vertical: 6 },
        padding: { horizontal: 12, vertical: 6 },
        maxContainerWidth: screenDimensions.width - 32,
        searchMinWidth: 280,
        showAllComponents: false,
        maxVisibleComponents: 3,
      },
      md: {
        containerHeight: Platform.OS === 'android' ? 60 : 52,
        searchBorderRadius: 10,
        iconSize: 22,
        buttonSize: 38,
        minWidth: 500,
        spacing: { horizontal: 12, vertical: 8 },
        padding: { horizontal: 16, vertical: 8 },
        maxContainerWidth: Math.min(screenDimensions.width - 64, 900),
        searchMinWidth: 300,
        showAllComponents: true,
        maxVisibleComponents: 4,
      },
      lg: {
        containerHeight: Platform.OS === 'android' ? 64 : 56,
        searchBorderRadius: 12,
        iconSize: 24,
        buttonSize: 40,
        spacing: { horizontal: 16, vertical: 10 },
        padding: { horizontal: 20, vertical: 10 },
        maxContainerWidth: Math.min(screenDimensions.width - 80, 1100),
        searchMinWidth: 400,
        showAllComponents: true,
        maxVisibleComponents: 5,
      },
      xl: {
        containerHeight: Platform.OS === 'android' ? 68 : 60,
        searchBorderRadius: 14,
        iconSize: 26,
        buttonSize: 42,
        spacing: { horizontal: 20, vertical: 12 },
        padding: { horizontal: 24, vertical: 12 },
        maxContainerWidth: Math.min(screenDimensions.width - 96, 1200),
        searchMinWidth: 450,
        showAllComponents: true,
        maxVisibleComponents: 6,
      },
      xxl: {
        containerHeight: Platform.OS === 'android' ? 72 : 64,
        searchBorderRadius: 16,
        iconSize: 28,
        buttonSize: 46,
        spacing: { horizontal: 24, vertical: 14 },
        padding: { horizontal: 28, vertical: 14 },
        maxContainerWidth: Math.min(screenDimensions.width - 112, 1400),
        searchMinWidth: 500,
        showAllComponents: true,
        maxVisibleComponents: 8,
      },
    };

    const config = configs[currentBreakpoint];

    if (Platform.OS === 'web') {
      // Web search bar is always dense/compact with square ends (edge fade applied in styles).
      const rowHeight = responsiveHeight(WEB_SEARCH_ROW_DENSE_DP);
      config.containerHeight = rowHeight;
      (config as any).maxHeight = rowHeight + responsivePadding(4) * 2;
      config.spacing = {
        ...config.spacing,
        vertical: 2,
        horizontal: Math.min(config.spacing.horizontal, 8),
      };
      config.padding = {
        ...config.padding,
        vertical: 2,
        horizontal: Math.min(config.padding.horizontal, 12),
      };
      config.iconSize = Math.min(config.iconSize, 18);
      config.buttonSize = Math.min(config.buttonSize, 32);
      config.searchBorderRadius = 0;
    }

    // Store base container height in shared value for use in animated style
    baseContainerHeight.value = config.containerHeight;

    return config;
  }, [currentBreakpoint, screenDimensions.width, baseContainerHeight, isTablet, compact]);

  // Compact icons on web (always dense) and native mobile / explicit compact rows
  const useCompactIcons = useMemo(() =>
    compact ||
    Platform.OS === 'web' ||
    (Platform.OS !== 'web' && !isTablet && (currentBreakpoint === 'xs' || currentBreakpoint === 'sm')),
    [compact, currentBreakpoint, isTablet]
  );

  // Calculate base container width for burger menu calculation
  const baseContainerWidth = useMemo(() => {
    // For tablet full-width mode, use screen width minus padding
    // Otherwise use the responsive config maxContainerWidth
    if (isTablet && fullWidthOnTablet) {
      const containerPadding = 20;
      return screenDimensions.width - (containerPadding * 2);
    }
    return responsiveConfig.maxContainerWidth;
  }, [isTablet, fullWidthOnTablet, screenDimensions.width, responsiveConfig.maxContainerWidth]);

  // Calculate if burger menu should be shown
  const showBurgerMenu = useMemo(
    () =>
      computeShowBurgerMenu({
        customIcons,
        disableBurger,
        isTablet,
        fullWidthOnTablet,
        baseContainerWidth,
        responsiveConfig,
        otherComponents,
        extraComponents,
        aiSearch,
      }),
    [
      customIcons,
      disableBurger,
      isTablet,
      fullWidthOnTablet,
      baseContainerWidth,
      responsiveConfig.buttonSize,
      responsiveConfig.spacing.horizontal,
      aiSearch,
      otherComponents,
      responsiveConfig.showAllComponents,
      responsiveConfig.maxVisibleComponents,
      responsiveConfig.searchMinWidth,
      responsiveConfig.padding.horizontal,
      extraComponents,
    ],
  );

  const paginationModalFooter = useMemo(() => {
    if (!pagination) return undefined;
    return <SearchBarPaginationModalBlock config={pagination} />;
  }, [pagination]);

  useEffect(() => {
    if (
      !pagination ||
      computeShowBurgerMenu({
        customIcons,
        disableBurger,
        isTablet,
        fullWidthOnTablet,
        baseContainerWidth,
        responsiveConfig,
        otherComponents,
        extraComponents,
        aiSearch,
      }) ||
      (shouldExpand && isExpanded)
    ) {
      setPaginationExpanded(false);
    }
  }, [
    pagination,
    shouldExpand,
    isExpanded,
    customIcons,
    disableBurger,
    isTablet,
    fullWidthOnTablet,
    baseContainerWidth,
    responsiveConfig.buttonSize,
    responsiveConfig.spacing.horizontal,
    responsiveConfig.showAllComponents,
    responsiveConfig.maxVisibleComponents,
    responsiveConfig.searchMinWidth,
    responsiveConfig.padding.horizontal,
    aiSearch,
    otherComponents,
    extraComponents,
  ]);

  // Calculate component widths based on available space
  const componentWidths = useMemo(() => {
    const hasCustomIcons = customIcons && customIcons.length > 0;
    const hasOtherComponents = otherComponents && otherComponents.length > 0;
    const hasActionButton = !!actionButton;
    const hasExtraComponents = !!extraComponents;

    // Use the same base container width calculation as burger menu
    // For tablet full-width mode, use screen width minus padding
    // Otherwise use the responsive config maxContainerWidth
    const containerPadding = isTablet && fullWidthOnTablet ? 20 : responsiveConfig.padding.horizontal;
    const maxContainerWidth = baseContainerWidth;

    let totalReservedWidth = containerPadding * 2; // Container padding

    // Calculate icon container width - account for actual icon size + gap from GenerateArray
    const webDenseIcons = Platform.OS === 'web' && useCompactIcons;
    const iconSize = webDenseIcons ? WEB_DENSE_ICON_SIZE : (useCompactIcons ? 36 : 48);
    const iconGap = webDenseIcons ? WEB_DENSE_ICON_GAP : (useCompactIcons ? 8 : 12);
    const iconCount = customIcons?.length || 0;
    const calculatedIconWidth = iconCount > 0 ? iconCount * (iconSize + iconGap) + iconGap : 0; // Add extra gap for padding
    // Cap the width but ensure it's large enough for all icons
    const iconContainerWidth = hasCustomIcons ?
      Math.max(calculatedIconWidth, Math.min(calculatedIconWidth, aiSearch ? 180 : 250)) : 0;

    // Calculate other components width (hide when burger is shown)
    const maxOtherComponents = responsiveConfig.showAllComponents ?
      (otherComponents?.length || 0) : responsiveConfig.maxVisibleComponents;
    const otherComponentsWidth = (hasOtherComponents && !showBurgerMenu) ?
      Math.min(maxOtherComponents * (responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal), aiSearch ? 150 : 250) : 0;

    // Calculate action button width
    const actionButtonWidth = hasActionButton ? responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal : 0;

    // Calculate extra components width (hide when burger is shown)
    const extraComponentsWidth = (hasExtraComponents && !showBurgerMenu) ? responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal : 0;

    totalReservedWidth += iconContainerWidth + otherComponentsWidth + actionButtonWidth + extraComponentsWidth;

    // Calculate available width in parent container
    const availableWidth = maxContainerWidth - totalReservedWidth;

    // When aiSearch is present, search takes 80-85% of available width in parent
    // Otherwise, use all available width
    const searchWidth = aiSearch
      ? Math.max(responsiveConfig.searchMinWidth, availableWidth * 0.82) // 82% of available width
      : Math.max(responsiveConfig.searchMinWidth, availableWidth);

    return {
      container: maxContainerWidth,
      search: searchWidth,
      icons: iconContainerWidth,
      otherComponents: otherComponentsWidth,
      actionButton: actionButtonWidth,
      extraComponents: extraComponentsWidth,
    };
  }, [
    responsiveConfig,
    customIcons,
    otherComponents,
    actionButton,
    extraComponents,
    showBurgerMenu,
    aiSearch,
    isTablet,
    fullWidthOnTablet,
    baseContainerWidth,
    useCompactIcons,
  ]);

  // Enhanced container styles
  const containerStyles = useMemo(() => {
    // Base top position - always use the same value to prevent position shifts
    // This value should NEVER change, regardless of expansion state
    const baseTop = Platform.OS === 'ios' ? 15 : 20;

    // Check if customStyle provides left positioning (to avoid right conflict)
    const hasCustomLeft = customStyle?.left !== undefined;

    const stretchFullWidth = fullWidthOnTablet || compact;
    const baseMaxWidth = (isTablet && stretchFullWidth) || (Platform.OS === 'web' && stretchFullWidth)
      ? undefined
      : (isTablet ? (shouldExpand ? Math.min(screenDimensions.width * 0.9, 1000) : 400) : componentWidths.container);
    const effectiveMaxWidth = Platform.OS !== 'web' && !isTablet
      ? Math.min(componentWidths.container, screenDimensions.width - (responsiveConfig.padding.horizontal * 2))
      : baseMaxWidth;

    return {
      ...componentStyles.container,
      ...(effectiveMaxWidth !== undefined && { maxWidth: effectiveMaxWidth }),
      width: Platform.OS === 'web' || (isTablet && stretchFullWidth) ? '100%' as const : undefined,
      // CRITICAL: Always set base height in layout - this is the layout footprint
      // The animated style will override the visual height when expanded, but layout stays constant
      // This ensures the component doesn't push content down when expanded
      minHeight: responsiveConfig.containerHeight,
      ...(Platform.OS === 'web' &&
        responsiveConfig.maxHeight != null &&
        !(shouldExpand && isExpanded) && {
          maxHeight: responsiveConfig.maxHeight,
          height: responsiveConfig.containerHeight,
        }),
      height: Platform.OS === 'web' && !(shouldExpand && isExpanded)
        ? responsiveConfig.containerHeight
        : undefined,
      // Ensure the container doesn't affect layout flow when expanded
      // Since it's absolutely positioned, it's already out of flow, but this reinforces it
      // Use appropriate padding for tablet full-width mode to prevent content from touching edges
      // Platform-specific positioning
      ...(Platform.OS === 'web' ? {
        // Keep positioning stable across expand/collapse to avoid layout flash
        // and preserve alignment with the original search bar position.
        position: 'relative' as const,
        alignSelf: 'stretch' as const,
        ...(!(shouldExpand && isExpanded) && !stretchFullWidth && { marginHorizontal: 'auto' as const }),
        ...(marginBelow != null && { marginBottom: responsivePadding(marginBelow) }),
        ...(shouldExpand && isExpanded ? {
          // Expand in place instead of jumping to viewport center.
          top: 0,
          left: 0,
          transform: [] as any,
          width: '100%' as const,
          maxWidth: isTablet ? 400 : componentWidths.container,
          zIndex: 1000,
        } : {}),
      } : {
        position: 'absolute' as const,
          // CRITICAL: Always use the same top position - NEVER change it
          // This prevents any position shifts on blur/collapse
          top: baseTop,
          // For tablet full-width mode, position relative to parent (which is full width)
          // For other cases, use padding-based positioning
          left: isTablet && fullWidthOnTablet ? 0 : responsiveConfig.padding.horizontal,
          // Only set right if customStyle doesn't provide left (to avoid conflicts)
          ...(!hasCustomLeft && {
            right: isTablet && fullWidthOnTablet ? 0 : responsiveConfig.padding.horizontal,
          }),
          // Always use high z-index to ensure it overlays content when expanded
          zIndex: 1000,
          // Ensure overflow is visible so expanded content can show
          overflow: 'visible' as const,
          // The container is absolutely positioned, so it's out of the normal flow
          // This means expansion won't push content down - it will overlay instead
      }),
      // Apply customStyle last to ensure it overrides all default styles
      ...customStyle,
    };
  }, [
    customStyle,
    marginBelow,
    componentWidths.container,
    responsiveConfig.containerHeight,
    responsiveConfig.padding.horizontal,
    shouldExpand,
    isExpanded,
    fullWidthOnTablet,
    compact
  ]);

  // Enhanced search container styles
  const hasSearchInputReplacement = !!searchInputReplacement;
  const searchIconsOnly =
    !showSearch && (customIcons?.length ?? 0) > 0 && !(shouldExpand && isExpanded);
  const searchGlass = !removeBackgroundColor && !searchIconsOnly;

  const searchContainerStyles = useMemo(() => {
    const baseMinWidth = searchOnly ? undefined : Math.max(minWidth || responsiveConfig.searchMinWidth, responsiveConfig.searchMinWidth);
    const isNative = Platform.OS !== 'web';
    const searchBorderRadius = isNative ? responsiveConfig.searchBorderRadius : 0;

    const nativeBurgerRow =
      isNative && showBurgerMenu && !(isExpanded && shouldExpand);

    const replacementMinHeight = hasSearchInputReplacement
      ? Math.max(
          responsiveConfig.containerHeight,
          Platform.OS === 'web'
            ? responsiveConfig.containerHeight
            : responsivePadding(52),
        )
      : responsiveConfig.containerHeight;

    const replacementOnly = hasSearchInputReplacement && !showSearch && !collapsableComponent;

    const iconsOnlyNoSearch = searchIconsOnly;

    const glass = searchGlass;
    // Borders are inside the box, so add them back to keep the row from clipping the input.
    const glassBorderWidth = glass ? 1 : 0;

    const collapsedRowMinHeight =
      (replacementOnly
        ? replacementMinHeight + responsivePadding(4)
        : replacementMinHeight + responsiveConfig.spacing.vertical * 2) +
      glassBorderWidth * 2 +
      (isNative && hideInput && (otherComponents?.length ?? 0) > 0
        ? responsivePadding(14)
        : 0);
    const expandedRowMinHeight = expandedHeightValue - responsivePadding(72);

    const horizontalPad = hideInput ? 16 : responsiveConfig.padding.horizontal;
    const webEdgeFade =
      Platform.OS === 'web' && !(shouldExpand && isExpanded) && !iconsOnlyNoSearch
        ? {
            // Sharp left/right fade-out at the bar ends (square, no radius).
            maskImage:
              'linear-gradient(to right, transparent 0%, #000 10px, #000 calc(100% - 10px), transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, #000 10px, #000 calc(100% - 10px), transparent 100%)',
            maskSize: '100% 100%',
            WebkitMaskSize: '100% 100%',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
          }
        : null;

    return {
      ...componentStyles.searchContainer,
      paddingHorizontal: horizontalPad,
      ...(glass
        ? { ...getFrostedGlassStyle(theme), borderWidth: glassBorderWidth }
        : { backgroundColor: 'transparent' }),
      minWidth: baseMinWidth,
      // Native + hamburger: omit full-width so the row can flex; width:'100%' fights the burger sibling.
      ...(Platform.OS === 'web'
        ? { width: '100%' as const }
        : nativeBurgerRow
          ? {}
          : { width: '100%' as const }),
      // Outer minHeight must include paddingVertical so inner row (minHeight = containerHeight) fits;
      // otherwise the TextInput clips (e.g. Integrations search).
      minHeight:
        shouldExpand && isExpanded ? expandedRowMinHeight : collapsedRowMinHeight,
      height: Platform.OS === 'web' && !(shouldExpand && isExpanded)
        ? collapsedRowMinHeight
        : 'auto',
      flex: 0,
      maxHeight:
        Platform.OS === 'web'
          ? shouldExpand && isExpanded
            ? expandedHeightValue
            : responsiveConfig.maxHeight
          : shouldExpand && isExpanded && !collapsableComponent
            ? expandedHeightValue - 80
            : undefined,
      borderRadius: searchBorderRadius,
      paddingVertical: shouldExpand && isExpanded
        ? responsiveConfig.spacing.vertical * 2
        : replacementOnly
          ? responsivePadding(4)
          : responsiveConfig.spacing.vertical,
      overflow:
        Platform.OS === 'web' && !(shouldExpand && isExpanded)
          ? ('hidden' as const)
          : ('visible' as const),
      // Android elevation shadows render through the translucent glass fill.
      elevation: 0,
      ...(Platform.OS === 'ios' &&
        (glass
          ? {
              shadowColor: theme ? '#000000' : '#ffffff',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: theme ? 0.1 : 0.05,
              shadowRadius: 12,
            }
          : {
              shadowOpacity: 0,
              shadowRadius: 0,
              shadowOffset: { width: 0, height: 0 },
            })),
      ...(webEdgeFade as any),
      ...(isExpanded && shouldExpand && {
        // Use measured collapsed search bar width so expanded matches it.
        minWidth: hideInput ? undefined : searchBarMeasuredWidth || componentWidths.container,
        width: (searchBarMeasuredWidth || componentWidths.container) as any,
        alignSelf: 'stretch' as const,
        zIndex: 100002,
      }),
    };
  }, [
    theme,
    minWidth,
    componentWidths.search,
    responsiveConfig,
    shouldExpand,
    isExpanded,
    expandedHeightValue,
    searchOnly,
    componentWidths.container,
    searchBarMeasuredWidth,
    showBurgerMenu,
    hasSearchInputReplacement,
    showSearch,
    customIcons,
    collapsableComponent,
    compact,
    hideInput,
    otherComponents,
    removeBackgroundColor,
    searchGlass,
  ]);

  // Handle container layout changes
  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setContainerLayout({ width, height });
  }, []);

  // Capture search bar width when collapsed so expanded state can use it as minWidth
  const handleSearchContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    if (!isExpanded && width > 0) {
      setSearchBarMeasuredWidth(width);
    }
  }, [isExpanded]);

  // Render other components with improved responsive behavior
  const renderOtherComponents = useCallback(() => {
    if (!otherComponents) return null;

    const maxVisible = responsiveConfig.showAllComponents ?
      otherComponents.length : responsiveConfig.maxVisibleComponents;
    const visibleComponents = otherComponents.slice(0, maxVisible);
    const isWeb = Platform.OS === 'web';
    // Keep action chips inside the dense web search row (no labels, no wrap).
    const webButtonSize = Math.max(
      20,
      Math.min(WEB_DENSE_ICON_SIZE, responsiveConfig.containerHeight - responsivePadding(12)),
    );
    const webIconTile = Math.max(14, webButtonSize - 8);
    const webIconSize = Math.max(11, webIconTile - 3);

    return (
      <View style={[
        componentStyles.otherComponentsContainer,
        {
          maxWidth: '100%',
          justifyContent: screenDimensions.width < 576 ? 'center' : 'flex-start',
          flexGrow: screenDimensions.width < 576 ? 1 : 0,
          gap: isWeb ? WEB_DENSE_ICON_GAP : 8,
          flexWrap: isWeb ? 'nowrap' : 'nowrap',
          flexDirection: 'row',
          alignItems: 'flex-end',
          paddingHorizontal: isWeb ? responsivePadding(4) : 4,
          ...(isWeb && {
            height: responsiveConfig.containerHeight,
            maxHeight: responsiveConfig.containerHeight,
            overflow: 'hidden' as const,
            flexShrink: 0,
          }),
        }
      ]}>
        {visibleComponents.map((component, index) => {
          if (component.functionName === 'showHelp') return null;

          const label = component.label
            ? component.label
            : component.functionName
              .replace(/([a-z])([A-Z])/g, '$1 $2')
              .replace(/_/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase());

          const renderedIcon = React.isValidElement(component.icon)
            ? React.cloneElement(component.icon as any, {
                color: iconColorOnLightChip(
                  (component.icon as any).props?.color,
                  theme ? (isWeb ? '#3b82f6' : '#ffffff') : '#ffffff',
                  theme,
                ),
                size: (component.icon as any).props?.size
                  ? Math.min((component.icon as any).props.size, isWeb ? webIconSize : responsiveConfig.iconSize * 0.8)
                  : (isWeb ? webIconSize : responsiveConfig.iconSize * 0.8),
                style: [
                  (component.icon as any).props?.style,
                  isWeb && {
                    maxWidth: webIconTile,
                    maxHeight: webIconTile,
                  },
                ],
              })
            : component.icon;

          return (
            <View
              key={`${component.functionName}-${index}`}
              style={{
                flexShrink: 0,
                flexDirection: isWeb ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isWeb ? 0 : 2,
                ...(isWeb && {
                  height: webButtonSize,
                  maxHeight: webButtonSize,
                }),
              }}
            >
              <Pressable
                style={({ pressed }) => [
                  getPillButtonStyle(theme, isWeb ? 'secondary' : 'primary'),
                  {
                    minWidth: isWeb ? webButtonSize : 32,
                    width: isWeb ? webButtonSize : 32,
                    minHeight: isWeb ? webButtonSize : 32,
                    height: isWeb ? webButtonSize : 32,
                    maxHeight: isWeb ? webButtonSize : 32,
                    paddingHorizontal: 0,
                    paddingVertical: 0,
                    transform: [{ scale: pressed ? 0.95 : 1 }],
                    overflow: 'hidden' as const,
                    ...(Platform.OS === 'web' && {
                      transition: 'all 0.2s ease',
                    }),
                  }
                ]}
                onPress={() => {
                  setShowExtendedMenu(false);
                  component.function();
                }}
                accessibilityRole="button"
                accessibilityLabel={label || component.functionName}
                // @ts-expect-error web title tooltip
                title={isWeb ? label : undefined}
              >
                <View
                  style={[
                    {
                      borderRadius: 9999,
                      padding: 0,
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                    }
                  ]}
                >
                  {renderedIcon}
                </View>
              </Pressable>
              {/* Labels only on native — web keeps a single-line dense row */}
              {!isWeb && (
                <Text
                  style={{
                    fontSize: responsiveFontSize(10),
                    lineHeight: responsiveFontSize(12),
                    fontWeight: '500',
                    color: theme ? '#374151' : '#d1d5db',
                    textAlign: 'center',
                    maxWidth: 48,
                  }}
                  numberOfLines={1}
                >
                  {label}
                </Text>
              )}
            </View>
          );
        })}
      </View>
    );
  }, [
    otherComponents,
    responsiveConfig,
    componentWidths.otherComponents,
    theme,
    screenDimensions.width,
  ]);

  // Render search section
  const renderSearchSection = useCallback(() => {
    if (otherComponents && !showSearch && !searchInputReplacement && !collapsableComponent) return null;
    if (onlyOthers && !collapsableComponent) return null; // Don't render search when onlyOthers is active, unless we have a collapsable component

    const replacementRowMinHeight = hasSearchInputReplacement
      ? Math.max(
          responsiveConfig.containerHeight,
          Platform.OS === 'web'
            ? responsiveConfig.containerHeight
            : responsivePadding(52),
        )
      : responsiveConfig.containerHeight;
    const replacementOnly = hasSearchInputReplacement && !showSearch && !collapsableComponent;

    return (
      <View
        style={[
          componentStyles.searchSection,
          {
            flexGrow: 1,
            flexShrink: 1,
            zIndex: 10000,
            // For smaller screens (xs/sm), force search to take available width or 100% when wrapped
            minWidth: screenDimensions.width < 576 ? '100%' : responsiveConfig.searchMinWidth,
            width: 'auto',
            minHeight:
              shouldExpand && isExpanded
                ? expandedHeightValue - responsivePadding(72)
                : replacementRowMinHeight,
            ...(Platform.OS === 'web' &&
              !(shouldExpand && isExpanded) && {
                maxHeight: responsiveConfig.containerHeight,
              }),
          }
        ]}
        pointerEvents="box-none"
      >
        {searchInputReplacement && !collapsableComponent ? (
          <View
            style={{
              flex: 1,
              width: '100%',
              justifyContent: 'center',
              minHeight: replacementOnly ? undefined : replacementRowMinHeight,
            }}
          >
            {searchInputReplacement}
          </View>
        ) : null}
        {(showSearch || collapsableComponent) && !searchInputReplacement ? (
          <InteractiveSearch
            zIndex={100}
            collapsedContent={collapsedContent}
            aiCallback={wrappedAICallback as any}
            aiSearch={aiSearch}
            placeHolder={placeHolder}
            searchParams={searchParams}
            callback={handleSearchCallback}
            onValueChange={handleInputValueChange}
            onFocus={handleInputFocus}
            ref={collapsableComponentRef}
            onBlur={handleInputBlur}
            isExpanded={isExpanded}
            multiline={shouldExpand && isExpanded}
            customIconsCount={customIcons?.length || 0}
            value={searchState}
            inputBorderRadius={Platform.OS === 'web' ? 0 : responsiveConfig.searchBorderRadius}
            inputMinHeight={responsiveConfig.containerHeight}
            onSubmitAI={shouldExpand && aiSearch ? handleWandPress : undefined}
            collapsableComponent={collapsableComponent}
            hideInput={hideInput}
          />
        ) : null}
      </View>
    );
  }, [
    otherComponents,
    showSearch,
    onlyOthers,
    collapsableComponent,
    responsiveConfig.containerHeight,
    aiCallback,
    aiSearch,
    placeHolder,
    searchParams,
    handleSearchCallback,
    shouldExpand,
    isExpanded,
    expandedHeightValue,
    handleInputFocus,
    handleInputBlur,
    responsiveConfig,
    customIcons,
    handleWandPress,
    searchInputReplacement,
    hasSearchInputReplacement,
    showSearch,
    collapsableComponent,
  ]);

  const expandedIconButtonStyle = useMemo(() => {
    const size = responsiveConfig.buttonSize * 0.7;
    return {
      ...getPillButtonStyle(theme, 'secondary'),
      paddingHorizontal: 0,
      paddingVertical: 0,
      width: size,
      height: size,
    };
  }, [theme, responsiveConfig.buttonSize]);

  const forceCollapse = useCallback(() => {
    collapsableComponentRef.current?.forceCollapse();
  }, [collapsableComponentRef]);

  // Prefer caller callback (DefaultScreen already applies safe-back rules).
  // Gating on canSafelyGoBack first incorrectly opens the menu when a nested
  // stack screen (e.g. DataBrowser) has no local history but a custom back target.
  const handleBackPress = useCallback(() => {
    if (typeof backButtonCallback === 'function') {
      backButtonCallback();
      return;
    }
    if (!canSafelyGoBack(navigation)) {
      dispatch({ type: OPEN_MENU });
      return;
    }
    navigation.goBack();
  }, [backButtonCallback, navigation, dispatch]);

  return (
    <>
      {/* Backdrop - only show when expanded and aiCallback exists */}
      {shouldExpand && isExpanded && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            },
            backdropAnimatedStyle,
          ]}
        >
          <Pressable
            style={{ flex: 1 }}
            onPress={collapsableComponent ? forceCollapse : handleBackdropPress}
            accessibilityLabel={t('closeSearch')}
            accessibilityRole="button"
          />
        </Animated.View>
      )}

      {/* Main Container */}
      <Animated.View
        ref={containerRef}
        onLayout={handleContainerLayout}
        style={[
          containerStyles,
          // Always apply animated style when shouldExpand is true
          // It will use base height when collapsed and expanded height when expanded
          shouldExpand ? containerAnimatedStyle : {}
        ]}
        key={`search-responsive-${currentBreakpoint}-${screenDimensions.width}`}
      >
      <View
        style={{
          width: '100%',
          alignSelf: 'stretch',
          flexDirection: 'column',
        }}
      >
      <View style={[
        componentStyles.content,
        {
          flexDirection: Platform.select({ native: inverse ? 'row-reverse' : 'row' }),
          gap:
            showBurgerMenu && Platform.OS !== 'web'
              ? responsivePadding(6)
              : responsiveConfig.spacing.horizontal,
          alignItems: 'center',
          // Burger row: pack controls so the menu sits next to the search; space-between
          // spreads children across the full width and leaves a large gap on native.
          justifyContent: showBurgerMenu ? 'flex-start' : 'space-between',
          width: '100%',
        }
      ]}>
        {/* Back button - integrated into row on native when backButton is true */}
        {backButton && Platform.OS !== 'web' && (
          <SearchBarBackButton onPress={handleBackPress} style={backButtonStyle} />
        )}
        {/* Main search container */}
        <View
          ref={searchContainerRef}
          onLayout={handleSearchContainerLayout}
          style={[
          searchContainerStyles,
          showBurgerMenu ? {
            // Share the row with the burger (width handled in searchContainerStyles on native).
            flex: 1,
            flexGrow: 1,
            flexShrink: 1,
            minWidth: 0,
            zIndex: 10000,
          } : {
              flex: searchBarWidth ? 0 : 1
          }
        ]}>
              {searchGlass && (
                <FrostedGlassGlow
                  theme={theme}
                  clip
                  borderRadius={Platform.OS === 'web' ? 0 : responsiveConfig.searchBorderRadius}
                />
              )}
              <XStack 
            style={[
            componentStyles.innerContainer,
            {
              // When expanded with collapsable component, use column layout to let it take full width
              flexDirection: (isExpanded && collapsableComponent) ? 'column' : 'row',
              // When expanded for AI, change to column layout for right-side buttons
              flexWrap: (isExpanded && shouldExpand && !collapsableComponent) ? 'wrap-reverse' : 'nowrap',
              gap: (aiSearch && !collapsableComponent) ? 0 : responsiveConfig.spacing.horizontal / 2,
              minHeight:
                shouldExpand && isExpanded
                  ? expandedHeightValue - responsivePadding(72)
                  : hasSearchInputReplacement
                    ? Math.max(
                        responsiveConfig.containerHeight,
                        Platform.OS === 'web'
                          ? responsiveConfig.containerHeight
                          : responsivePadding(52),
                      )
                    : responsiveConfig.containerHeight,
              ...(Platform.OS === 'web' &&
                !(shouldExpand && isExpanded) && {
                  maxHeight: responsiveConfig.maxHeight,
                }),
              alignItems: shouldExpand && isExpanded ? 'stretch' : 'center',
              alignContent: 'center',
              position: 'relative' as const,
              ...(Platform.OS === 'web' && {
                // Ensure search bar takes full width on wrap on smaller screens
                justifyContent: screenDimensions.width < 576 ? 'center' : 'flex-start',
              })
            }
          ]}>
                {/* When hideInput + pageTitle: pageTitle on left, otherComponents on right */}
            {hideInput && pageTitle ? (
              <>
                <View
                  style={{
                    flex: 1,
                    minWidth: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'row',
                    gap: responsivePadding(8),
                  }}
                >
                  <MaterialIcons
                    name="text-format"
                    size={isTablet ? 22 : 20}
                    color={theme ? '#1f2937' : '#f3f4f6'}
                  />
                  <Text
                    numberOfLines={1}
                    style={{
                      flex: 1,
                      fontSize: responsiveFontSize(isTablet ? 17 : 16),
                      fontWeight: '600',
                      color: theme ? '#111827' : '#f9fafb',
                    }}
                  >
                    {pageTitle}
                  </Text>
                </View>
                {/* Flex spacer pushes otherComponents to the right */}
                {otherComponents && otherComponents.length > 0 && (
                  <View style={{ flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
                    <View
                      style={{
                        flexShrink: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...(Platform.OS === 'web' && {
                          height: responsiveConfig.containerHeight,
                          maxHeight: responsiveConfig.containerHeight,
                          overflow: 'hidden' as const,
                        }),
                      }}
                    >
                      {hideInput ? renderOtherComponents() : null}
                    </View>
                  </View>
                )}
              </>
            ) : null}

            {/* Other components section - only when NOT hideInput */}
            {!hideInput && (otherComponents || onlyOthers) && !showBurgerMenu && !isExpanded && (
              <View
                style={{
                  flexShrink: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(Platform.OS === 'web' && {
                    height: responsiveConfig.containerHeight,
                    maxHeight: responsiveConfig.containerHeight,
                    overflow: 'hidden' as const,
                    alignSelf: 'center',
                  }),
                }}
              >
                {renderOtherComponents()}
              </View>
            )}

            {/* Custom icons - left of search (only when NOT expanded and NOT using burger menu) */}
              {customIcons && customIcons.length > 0 && !showBurgerMenu && !(shouldExpand && isExpanded) && Platform.OS !='web' && (
              <View
                style={[
                  componentStyles.customIconsContainer,
                  {
                    minWidth: (hideInput && !showSearch && disableBurger) ? '100%' : componentWidths.icons,
                    flex: (hideInput && !showSearch && disableBurger) ? 1 : 0,
                    height: (hideInput && !showSearch && disableBurger) ? 'auto' : responsiveConfig.containerHeight,
                    alignItems: 'center',
                        paddingHorizontal: "5%",
                    justifyContent: (hideInput && !showSearch && disableBurger) ? 'center' : 'center',
                    marginLeft: aiSearch ? -16 : 0,
                    flexShrink: (hideInput && !showSearch && disableBurger) ? 1 : 0,
                    overflow: 'visible',
                    ...((hideInput && !showSearch && disableBurger) ? { paddingVertical: 8 } : null),
                  }
                ]}
              >
                <GenerateArray
                  customIconStyle={customIconStyle}
                  disableBurger={disableBurger}
                  customIcons={customIcons}
                  setShowExtendedMenu={setShowExtendedMenu}
                      width={(hideInput && !showSearch && disableBurger) ? undefined : componentWidths.icons}
                  extraComponents={extraComponents}
                  otherComponents={otherComponents}
                  compact={useCompactIcons}
                      scrollableWithLabels={hideInput && !showSearch && disableBurger}
                />
              </View>
            )}

            {/* Extra components - left of search (hide when burger menu is shown or expanded) */}
              {extraComponents && !showBurgerMenu && !(shouldExpand && isExpanded) && (
              <View style={[
                componentStyles.extraComponentsContainer,
                {
                  height: responsiveConfig.containerHeight - 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              ]}>
                {extraComponents}
              </View>
            )}

                {/* Search section - don't render when onlyOthers is active, unless we have a collapsable component */}
                {(!onlyOthers || !!collapsableComponent) && (showSearch || !!searchInputReplacement || !!collapsableComponent) ? (
                <View style={{
                  flex: searchBarWidth ? 0 : 1,
                    width: searchBarWidth ? searchBarWidth : '85%',
                  // When expanded, add right padding to make room for button column
                    paddingRight: (shouldExpand && isExpanded && !collapsableComponent) ? 60 : 0,
                }}>
                    {renderSearchSection()}
                  </View>
                ) : collapsableComponent}

            {/* Right-aligned extra components (e.g. RangePicker) */}
            {rightExtraComponents && !showBurgerMenu && !(shouldExpand && isExpanded) && (
              <View
                style={{
                  flexShrink: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: 'auto',
                }}
              >
                {rightExtraComponents}
              </View>
            )}

              {/* Right-side button column when expanded */}
              {shouldExpand && isExpanded && (
                <View style={{
                  position: 'absolute' as const,
                  right: responsiveConfig.spacing.horizontal,
                  top: responsiveConfig.spacing.vertical,
                  flexDirection: 'column' as const,
                  alignItems: 'center' as const,
                  gap: responsiveConfig.spacing.vertical,
                  zIndex: 100,
                }}>
                  {/* Close button */}
                  <Pressable
                    onPress={handleCollapse}
                    style={expandedIconButtonStyle}
                    accessibilityLabel={t('closeSearch')}
                    accessibilityRole="button"
                  >
                    <Feather
                      name="x"
                      size={responsiveConfig.iconSize * 0.65}
                      color={theme ? '#333' : '#ccc'}
                    />
                  </Pressable>

                  {/* Custom icons - middle of column */}
                  {customIcons && customIcons.length > 0 && !showBurgerMenu && (
                    <View
                      style={[
                        {
                          flexDirection: 'column' as const,
                          alignItems: 'center' as const,
                          gap: responsiveConfig.spacing.vertical / 2,
                          minWidth: responsiveConfig.buttonSize * 0.7 * customIcons.length,
                        }
                      ]}
                    >
                      {customIcons.map((icon, index) => (
                        <Pressable
                          key={index}
                          onPress={() => icon.function()}
                          style={[
                            expandedIconButtonStyle,
                            { width: undefined, minWidth: expandedIconButtonStyle.width },
                          ]}
                        >
                          {React.isValidElement(icon.icon)
                            ? React.cloneElement(icon.icon as any, {
                              color: iconColorOnLightChip(
                                (icon.icon as any).props?.color,
                                theme ? '#374151' : '#d1d5db',
                                theme,
                              ),
                              size: (icon.icon as any).props?.size ?? (responsiveConfig.iconSize * 0.7),
                            })
                            : icon.icon}
                        </Pressable>
                      ))}
                    </View>
                  )}
                    {/* Clear button - appended to column (only if input is not hidden) */}
                    {!hideInput && (
                      <Pressable
                        onPress={handleClearSearch}
                        style={[
                          expandedIconButtonStyle,
                          { opacity: liveSearchValue?.trim() ? 1 : 0.4 },
                        ]}
                        disabled={!liveSearchValue?.trim()}
                        accessibilityLabel={t('clearSearch')}
                        accessibilityRole="button"
                      >
                        <MaterialIcons
                          name="cancel"
                          size={responsiveConfig.iconSize * 0.65}
                          color={liveSearchValue?.trim() ? (theme ? '#333' : '#ccc') : 'rgba(128,128,128,0.5)'}
                        />
                      </Pressable>
                    )}
                    {/* Wand button - when aiSearch and input is not hidden */}
                    {aiSearch && !hideInput && (
                    <Pressable
                      onPress={handleWandPress}
                      disabled={!liveSearchValue?.trim() || aiWandLoading}
                      style={[
                        expandedIconButtonStyle,
                        { opacity: liveSearchValue?.trim() && !aiWandLoading ? 1 : 0.4 },
                      ]}
                      accessibilityLabel={t('aiSearch')}
                      accessibilityRole="button"
                    >
                      {aiWandLoading ? (
                        <LoadingIndicator type={undefined} color={theme ? '#3b82f6' : '#60a5fa'} />
                      ) : (
                        <FontAwesome
                          name="magic"
                          size={responsiveConfig.iconSize * 0.65}
                          color={theme ? (liveSearchValue?.trim() ? '#374151' : '#9ca3af') : (liveSearchValue?.trim() ? '#d1d5db' : '#6b7280')}
                        />
                      )}
                    </Pressable>
                  )}
                </View>
              )}
                {customIcons && customIcons.length > 0 && !showBurgerMenu && !(shouldExpand && isExpanded) && Platform.OS === 'web' && (
                  <View
                    style={[
                      componentStyles.customIconsContainer,
                      {
                        minWidth: (hideInput && !showSearch && disableBurger) ? '100%' : componentWidths.icons,
                        flex: (hideInput && !showSearch && disableBurger) ? 1 : 0,
                        height:
                          hideInput && !showSearch && disableBurger
                            ? Platform.OS === 'web'
                              ? responsiveConfig.maxHeight
                              : 'auto'
                            : responsiveConfig.containerHeight,
                        maxHeight:
                          Platform.OS === 'web' ? responsiveConfig.maxHeight : undefined,
                        alignItems: 'center',
                        justifyContent: (hideInput && !showSearch && disableBurger) ? 'flex-start' : 'center',
                        marginLeft: aiSearch ? -16 : 0,
                        flexShrink: (hideInput && !showSearch && disableBurger) ? 1 : 0,
                        overflow: 'visible',
                      }
                    ]}
                  >
                    <GenerateArray
                      customIconStyle={customIconStyle}
                      disableBurger={disableBurger}
                      customIcons={customIcons}
                      setShowExtendedMenu={setShowExtendedMenu}
                      width={(hideInput && !showSearch && disableBurger) ? undefined : componentWidths.icons}
                      extraComponents={extraComponents}
                      otherComponents={otherComponents}
                      compact={useCompactIcons}
                      scrollableWithLabels={hideInput && !showSearch && disableBurger}
                    />
                  </View>
                )}
          </XStack>
        </View>

        {/* Pagination on same row as search (not a full-width row below) */}
        {pagination && !showBurgerMenu && !(shouldExpand && isExpanded) && (
          <View
            style={{
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'center',
              marginLeft: inverse ? 0 : responsivePadding(4),
              marginRight: inverse ? responsivePadding(4) : 0,
            }}
          >
            <SearchBarPaginationInline
              inline
              expanded={paginationExpanded}
              onExpandedChange={setPaginationExpanded}
              config={pagination}
              theme={theme}
              buttonSize={responsiveConfig.buttonSize}
              iconSize={responsiveConfig.iconSize}
              borderRadius={responsiveConfig.searchBorderRadius}
            />
          </View>
        )}

        {/* Burger menu button - positioned after search bar on mobile (combines customIcons, otherComponents, extraComponents) */}
        {((customIcons?.length ?? 0) > 0 || (otherComponents?.length ?? 0) > 0 || extraComponents) && showBurgerMenu && (
          <View
            style={[
              componentStyles.burgerMenuContainer,
              {
                height: responsiveConfig.containerHeight,
                justifyContent: 'center',
                alignItems: 'center',
                // Pull menu toward the search card (row: burger on right; row-reverse: burger on left)
                ...(Platform.OS !== 'web' && {
                  marginLeft: inverse ? 0 : -responsivePadding(8),
                  marginRight: inverse ? -responsivePadding(8) : 0,
                }),
              }
            ]}
          >
            <GenerateArray
              customIconStyle={customIconStyle}
              disableBurger={false}
              customIcons={customIcons}
              setShowExtendedMenu={setShowExtendedMenu}
              width={100}
              extraComponents={extraComponents}
              otherComponents={otherComponents}
              compact={useCompactIcons}
              modalExtraContent={showBurgerMenu ? paginationModalFooter : undefined}
            />
          </View>
        )}

        {/* Action button */}
        {actionButton && (
          <Pressable
            ref={buttonRef}
            disabled={!searchState}
            onPress={() => action?.()}
            style={[
              componentStyles.actionButton,
              getPillButtonStyle(theme, 'primary'),
              {
                opacity: searchState ? 1 : 0.5,
                width: responsiveConfig.buttonSize,
                height: responsiveConfig.buttonSize,
                paddingHorizontal: 0,
                paddingVertical: 0,
                marginLeft: responsiveConfig.spacing.horizontal,
              }
            ]}
            accessibilityRole="button"
            accessibilityLabel={t('Send')}
          >
            <Feather
              name="send"
              size={responsiveConfig.iconSize * 0.75}
              color="#ffffff"
            />
          </Pressable>
        )}
      </View>

      {pagination &&
        !showBurgerMenu &&
        !(shouldExpand && isExpanded) &&
        paginationExpanded && (
          <Animatable.View
            animation="fadeInDown"
            duration={280}
            useNativeDriver={Platform.OS !== 'web'}
            style={{
              width: '100%',
              alignSelf: 'stretch',
              paddingTop: responsivePadding(8),
            }}
          >
            <Pagination
              {...pagination}
              style={
                Platform.OS === 'web'
                  ? ({ width: '100%' } as any)
                  : { width: '100%' }
              }
            />
          </Animatable.View>
        )}

      </View>

      {/* Loading indicator */}
      {fetchingState && (
        <Animatable.View
          animation="fadeIn"
          style={[
            componentStyles.loadingContainer,
            {
              right: responsiveConfig.spacing.horizontal,
              top: (responsiveConfig.containerHeight - 20) / 2,
            }
          ]}
        >
          <LoadingIndicator
            type="indeterminate"
            color="#3b82f6"
          />
        </Animatable.View>
      )}

        {/* Close button is now in the right column when expanded */}
      </Animated.View>

      {/* AI Credits Modal */}
      <AICreditsModal
        open={showAICreditsModal}
        onClose={() => setShowAICreditsModal(false)}
        onPurchase={(packageId) => {
          // Close modal first
          setShowAICreditsModal(false);

          // Navigate to upgrade screen for purchasing credits
          // The modal shows credit packages, but actual purchase happens on upgrade screen
          try {
            (navigation as any).navigate('Settings', {
              screen: 'Upgrade',
              params: {
                initialTab: 'credits' // If your upgrade screen supports tab navigation
              }
            });
          } catch (error) {
            console.error('Failed to navigate to upgrade screen:', error);
          }
        }}
      />
    </>
  );
};

// Enhanced responsive styles
const componentStyles = StyleSheet.create({
  container: {
    zIndex: 1000,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    // Ensure overflow is visible so expanded content can overlay
    overflow: 'visible' as const,
  },
  content: {
    width: '100%',
    alignItems: 'center',
  },
  searchContainer: {
    overflow: 'visible',
    justifyContent: 'center',
    // Removed fixed top offset to allow natural flow
    alignItems: 'stretch',
  },
  innerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 40,
    alignContent: 'center',
  },
  otherComponentsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexShrink: 0,
    alignContent: 'center',
  },
  componentButton: {
    alignItems: 'center',
    justifyContent: 'center',
    // Modern hover effects for web
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
    }),
  },
  helpButton: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  searchSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    width: '100%',
  },
  customIconsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    alignContent: 'center',
  },
  extraComponentsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    alignContent: 'center',
  },
  burgerMenuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    minWidth: 48,
  },
  actionButton: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // Modern hover effects for web
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
    }),
  },
  loadingContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
});

// Memoize the component to prevent unnecessary re-renders
export default memo(SearchComponent, (prevProps, nextProps) => {
  // Custom comparison function
  return (
    prevProps.searchState === nextProps.searchState &&
    prevProps.fetchingState === nextProps.fetchingState &&
    prevProps.aiSearch === nextProps.aiSearch &&
    prevProps.showSearch === nextProps.showSearch &&
    prevProps.customIcons === nextProps.customIcons &&
    prevProps.extraComponents === nextProps.extraComponents &&
    prevProps.rightExtraComponents === nextProps.rightExtraComponents &&
    prevProps.lightTheme === nextProps.lightTheme &&
    prevProps.pagination === nextProps.pagination &&
    prevProps.collapsableComponent === nextProps.collapsableComponent &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.hideInput === nextProps.hideInput &&
    prevProps.searchInputReplacement === nextProps.searchInputReplacement &&
    prevProps.compact === nextProps.compact &&
    prevProps.fullWidthOnTablet === nextProps.fullWidthOnTablet &&
    prevProps.marginBelow === nextProps.marginBelow
  );
});